"use client"

import { push } from "@socialgouv/matomo-next"

import {
  ANALYTICS_DEFAULTS,
  ANALYTICS_ENABLED,
  ANALYTICS_WORKSPACE_STORAGE_KEY,
  EVENT_SCHEMA_VERSION,
} from "@/src/config/analytics"
import type { AnalyticsEventInput, AnalyticsIdentityContext, AnalyticsUserTier } from "@/src/lib/analytics/events"

function getStableWorkspaceId(): string {
  if (typeof window === "undefined") {
    return ANALYTICS_DEFAULTS.workspaceId
  }

  const cached = window.localStorage.getItem(ANALYTICS_WORKSPACE_STORAGE_KEY)
  if (cached) {
    return cached
  }

  const generated =
    typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `ws_${Math.random().toString(36).slice(2, 10)}`

  window.localStorage.setItem(ANALYTICS_WORKSPACE_STORAGE_KEY, generated)

  return generated
}

function getIdentityContext(): AnalyticsIdentityContext {
  if (typeof window === "undefined") {
    return {
      isAuthenticated: false,
    }
  }

  const bodyData = document.body?.dataset
  const path = window.location.pathname
  const isAuthenticated = bodyData?.analyticsIsAuthenticated
    ? bodyData.analyticsIsAuthenticated === "true"
    : path.startsWith("/app")

  if (!isAuthenticated) {
    return { isAuthenticated: false }
  }

  const userTier = (bodyData?.analyticsUserTier || ANALYTICS_DEFAULTS.userTier) as AnalyticsUserTier
  const workspaceId = bodyData?.analyticsWorkspaceId || getStableWorkspaceId()

  return {
    isAuthenticated,
    userTier,
    workspaceId,
  }
}

function applyEventVariables(event: AnalyticsEventInput, identity: AnalyticsIdentityContext): void {
  push(["setCustomVariable", 1, "EVENT_SCHEMA_VERSION", String(EVENT_SCHEMA_VERSION), "event"])

  const isAuthenticated = event.isAuthenticated ?? identity.isAuthenticated
  if (typeof isAuthenticated === "boolean") {
    push(["setCustomVariable", 2, "is_authenticated", String(isAuthenticated), "event"])
  }

  const userTier = event.userTier || identity.userTier
  if (userTier) {
    push(["setCustomVariable", 3, "user_tier", userTier, "event"])
  }

  const workspaceId = event.workspaceId || identity.workspaceId
  if (workspaceId) {
    push(["setCustomVariable", 4, "workspace_id", workspaceId, "event"])
  }
}

function serializeMetadata(metadata?: AnalyticsEventInput["metadata"]): string {
  if (!metadata) {
    return ""
  }

  const pairs = Object.entries(metadata)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${String(value)}`)

  if (pairs.length === 0) {
    return ""
  }

  return ` | ${pairs.join(";")}`
}

export function initializeClientAnalyticsContext(): void {
  if (!ANALYTICS_ENABLED || typeof window === "undefined") {
    return
  }

  const identity = getIdentityContext()

  push(["setCustomVariable", 1, "EVENT_SCHEMA_VERSION", String(EVENT_SCHEMA_VERSION), "visit"])

  if (typeof identity.isAuthenticated === "boolean") {
    push(["setCustomVariable", 2, "is_authenticated", String(identity.isAuthenticated), "visit"])
  }

  if (identity.userTier) {
    push(["setCustomVariable", 3, "user_tier", identity.userTier, "visit"])
  }

  if (identity.workspaceId) {
    push(["setCustomVariable", 4, "workspace_id", identity.workspaceId, "visit"])
  }
}

export function trackClientEvent(event: AnalyticsEventInput): void {
  if (!ANALYTICS_ENABLED || typeof window === "undefined") {
    return
  }

  const identity = getIdentityContext()
  applyEventVariables(event, identity)

  const nameWithMetadata = `${event.name}${serializeMetadata(event.metadata)}`
  const eventValue = event.value ?? EVENT_SCHEMA_VERSION

  push(["trackEvent", event.category, event.action, nameWithMetadata, eventValue])
}
